import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'

import { Button, Select } from '../../components/FormControls'
import { ErrorBanner, SuccessBanner, SkeletonRows, EmptyState } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'
import * as XLSX from 'xlsx'

// Shared tender-currency monetary formatter. See the matching comment in
// TenderWorkspacePage.jsx -- ideally belongs in ../../styles/tokens
// alongside formatNumber; defined locally here for the same reason
// (tokens.js not directly editable in this environment). Never converts
// -- only formats an already-correct-currency value and appends the
// currency code; a null/undefined currency renders without a suffix.
function formatMoney(value, currencyCode) {
  const formatted = formatNumber(value)
  return currencyCode ? `${formatted} ${currencyCode}` : formatted
}

// Cost Control review layer for the Direct Budget. This page performs
// NO financial calculation of its own: every Qty/Rate/Total shown here
// is read verbatim from build_costspace_export_payload() (the same
// v4-production SSOT used by the Validation Gate). The only write this
// page performs is Cost Code assignment on boq_item.canonical_cost_code_id
// -- classification only, never touching quantities, rates, or costs.
export default function DirectBudgetReviewPage() {
  const { tenderId, revisionId } = useParams()
  const [freezeSnapshotId, setFreezeSnapshotId] = useState(undefined)
  const [payload, setPayload] = useState(null)
  const [costCodes, setCostCodes] = useState([])
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [expanded, setExpanded] = useState(new Set())
  // Tender Currency -- single authoritative source for every monetary
  // display on this page. All qty/rate/total values shown here are
  // already in this currency (the financial SSOT normalizes at the
  // rate_buildup_line_cost_base level); this is display-only, never a
  // second conversion.
  const [tenderCurrency, setTenderCurrency] = useState(null)
  const [savingItem, setSavingItem] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    supabase.from('tender').select('currency').eq('id', tenderId).maybeSingle()
      .then(({ data }) => setTenderCurrency(data?.currency ?? null))
    const { data: rev, error: revErr } = await supabase
      .from('estimate_revision').select('status').eq('id', revisionId).single()
    if (revErr) return setError(revErr.message)

    const { data: snap, error: snapErr } = await supabase
      .from('freeze_snapshot')
      .select('id, full_boq_data')
      .eq('estimate_revision_id', revisionId)
      .maybeSingle()
    if (snapErr) return setError(snapErr.message)
    setFreezeSnapshotId(snap?.id ?? null)

    if (snap) {
      // Frozen path -- CORRECTED: build_costspace_export_payload() was
      // found to return direct_budget: [] for real frozen revisions
      // (confirmed directly: it reports RECONCILIATION_MISMATCH between
      // activity.total_budget and SUM(resource totals)=0, because
      // rate_buildup_line detail was never captured in freeze_snapshot
      // -- a separate, already-documented limitation -- and it also
      // requires tender_costspace_project_mapping, which is empty by
      // design). That RPC is export-specific tooling, not a reliable
      // read source for this review screen. Read directly from the
      // immutable full_boq_data instead, same split already proven for
      // BoqTab/TenderSummaryPage/RevisionComparisonPage: every value
      // below is verbatim from the snapshot or a simple division
      // (direct_cost / qty, matching boq_item_direct_unit_rate's own
      // formula) -- no financial recalculation, no second SSOT.
      const boqData = Array.isArray(snap.full_boq_data) ? snap.full_boq_data : []
      // rate_buildup_lines carries resource_code/resource_description as of
      // the Batch D freeze_estimate_revision extension. Older snapshots
      // simply won't have this key -- linesByBoqItem then resolves to an
      // empty array per item, same as before this change.
      const indirectForRates = snap.full_indirect_data && typeof snap.full_indirect_data === 'object' ? snap.full_indirect_data : {}
      const rateBuildupLines = Array.isArray(indirectForRates.rate_buildup_lines) ? indirectForRates.rate_buildup_lines : []
      const linesByBoqItem = {}
      for (const l of rateBuildupLines) {
        if (!l.boq_item_id) continue
        ;(linesByBoqItem[l.boq_item_id] ||= []).push(l)
      }
      const ccIds = [...new Set(boqData.map((it) => it.canonical_cost_code_id).filter(Boolean))]
      const { data: ccRows } = ccIds.length
        ? await supabase.from('canonical_cost_code').select('id, costspace_matched_code').in('id', ccIds)
        : { data: [] }
      const matchedById = Object.fromEntries((ccRows || []).map((r) => [r.id, r.costspace_matched_code]))

      const direct = boqData
        .filter((it) => !it.is_excluded)
        .map((it) => {
          const dc = Number(it.direct_cost) || 0
          const qty = Number(it.resolved_quantity) || 0
          return {
            boq_item_id: it.id,
            item_number: it.item_no,
            activity: {
              description: it.description, unit: it.unit, qty: it.resolved_quantity,
              unit_rate: qty !== 0 ? dc / qty : null,
              total_budget: dc,
              cost_code: it.canonical_cost_code_id ? (matchedById[it.canonical_cost_code_id] ?? null) : null,
            },
            // Rate Build-Up line detail, captured verbatim in freeze_snapshot
            // as of Batch D (consumption_factor, selected_rate, line_cost,
            // resource_code/resource_description). Same field mapping as the
            // Draft path below -- nothing is computed here as qty*rate.
            // row_type/unit are not part of the Batch D snapshot extension
            // (only resource_code/resource_description were added) -- left
            // undefined here rather than invented.
            resources: (linesByBoqItem[it.id] || []).map((l) => ({
              row_type: undefined,
              resource_code: l.resource_code ?? null,
              resource_description: l.resource_description ?? null,
              qty: qty * (Number(l.consumption_factor) || 0),
              unit: undefined,
              unit_rate: l.selected_rate,
              total: l.line_cost ?? null,
            })),
          }
        })
      setPayload({ direct_budget: direct, indirect_budget: [], validation: { errors: [], warnings: [], info: [] } })
    } else if (rev.status === 'draft') {
      // Draft path: no snapshot exists yet, so there is nothing to run
      // the RPC against. Build the SAME shape (boq_item_id, item_number,
      // activity{qty,unit,cost_code,total_budget}, resources[]) by
      // reading directly from the live SSOT tables -- boq_item,
      // boq_item_direct_cost, and rate_buildup_line/rate_buildup_line_cost.
      // This performs zero calculation: every value is a direct column
      // read, identical in meaning to what the RPC would expose after
      // freezing. Cost Control can review Draft data now; once frozen,
      // the page automatically switches to reading the immutable
      // snapshot above.
      const [boqRes, dcRes, rateRes] = await Promise.all([
        supabase.from('boq_item')
          .select('id, item_no, description, unit, resolved_quantity, is_excluded, canonical_cost_code_id')
          .eq('estimate_revision_id', revisionId)
          .eq('is_excluded', false)
          .order('item_no'),
        supabase.from('boq_item_direct_cost').select('boq_item_id, direct_cost'),
        // Activity Unit Cost Rate -- read verbatim from the
        // authoritative view, never computed in this component.
        supabase.from('boq_item_direct_unit_rate').select('boq_item_id, activity_unit_cost_rate'),
      ])
      if (boqRes.error) return setError(boqRes.error.message)
      const dcByItem = Object.fromEntries((dcRes.data || []).map((r) => [r.boq_item_id, r.direct_cost]))
      const rateByItem = Object.fromEntries((rateRes.data || []).map((r) => [r.boq_item_id, r.activity_unit_cost_rate]))

      const ccIds = [...new Set((boqRes.data || []).map((b) => b.canonical_cost_code_id).filter(Boolean))]
      const { data: ccRows } = ccIds.length
        ? await supabase.from('canonical_cost_code').select('id, costspace_matched_code').in('id', ccIds)
        : { data: [] }
      const matchedById = Object.fromEntries((ccRows || []).map((r) => [r.id, r.costspace_matched_code]))

      const direct = []
      for (const bi of boqRes.data || []) {
        // rate_buildup_line_cost is a computed VIEW (no real Foreign
        // Key to rate_buildup_line), so a nested select
        // (`.select('rate_buildup_line_cost(line_cost)')`) silently
        // fails -- PostgREST cannot embed a relation with no FK to
        // walk. Fixed by querying rate_buildup_line_cost directly and
        // joining the two result sets by rate_buildup_line.id in JS
        // (no calculation -- both values are read verbatim from the
        // same authoritative source the RPC uses after freezing).
        // Same shape AND same synthetic-row rules as
        // build_costspace_export_payload() (the frozen-path RPC) --
        // extended here to also cover method/factored_pct/
        // factored_reference_line_id/resource_quantity_output_id/
        // first_principles_detail_id, which the prior version of this
        // query did not select, so methods with no resource_library
        // resource (first_principles, all_in_subcontract, factored,
        // Output-Based) silently produced an empty resources array here
        // even though the RPC-side fix already covers them for the
        // frozen path. No calculation changes -- every value below is a
        // direct column read (rate_buildup_line_cost.line_cost,
        // first_principles_rate_breakdown.direct_unit_rate), identical
        // in meaning to the RPC.
        const { data: lineRows } = await supabase
          .from('rate_buildup_line')
          .select('id, method, consumption_factor, selected_rate, factored_pct, factored_reference_line_id, resource_quantity_output_id, first_principles_detail_id, resource_library(code, unit, resource_type, description)')
          .eq('boq_item_id', bi.id)
        const lineIds = (lineRows || []).map((l) => l.id)
        const [{ data: costRows }, { data: fpRows }] = await Promise.all([
          lineIds.length
            ? supabase.from('rate_buildup_line_cost').select('rate_buildup_line_id, line_cost').in('rate_buildup_line_id', lineIds)
            : Promise.resolve({ data: [] }),
          lineIds.length
            ? supabase.from('first_principles_rate_breakdown').select('rate_buildup_line_id, direct_unit_rate').in('rate_buildup_line_id', lineIds)
            : Promise.resolve({ data: [] }),
        ])
        const costByLineId = Object.fromEntries((costRows || []).map((c) => [c.rate_buildup_line_id, c.line_cost]))
        const fpRateByLineId = Object.fromEntries((fpRows || []).map((f) => [f.rate_buildup_line_id, f.direct_unit_rate]))

        const resourceBacked = (lineRows || []).filter((r) => r.resource_library)
        let resources = resourceBacked.map((r) => ({
          row_type: r.resource_library?.resource_type,
          resource_code: r.resource_library?.code,
          resource_description: r.resource_library?.description,
          qty: (bi.resolved_quantity || 0) * (r.consumption_factor || 0),
          unit: r.resource_library?.unit,
          unit_rate: r.selected_rate,
          total: costByLineId[r.id] ?? null,
        }))

        if (resources.length === 0) {
          // Factored lines need the REFERENCED line's own boq_item
          // description, not a generic placeholder (matches the RPC-side
          // behavior and the explicit "do not say 'Factored item'"
          // requirement). One extra lookup only when actually needed.
          const factoredRefLineIds = (lineRows || [])
            .filter((r) => r.method === 'factored' && r.factored_reference_line_id)
            .map((r) => r.factored_reference_line_id)
          // Same natural-type-only rule as build_costspace_export_payload()
          // (the frozen-path RPC): row_type is restricted to the four
          // existing resource_type values (material/labour/equipment/
          // subcontract) -- no invented classification values like
          // 'factored_pct' or 'first_principles_crew'. A Factored line
          // inherits its referenced line's own resource_type where that
          // reference has exactly one resource_library-backed line;
          // falls back to 'material' only when no single type is
          // determinable (e.g. the reference is itself multi-resource or
          // also synthetic).
          let refDescByLineId = {}
          let refTypeByLineId = {}
          if (factoredRefLineIds.length) {
            const { data: refLines } = await supabase
              .from('rate_buildup_line').select('id, boq_item_id, resource_library(resource_type)').in('id', factoredRefLineIds)
            const refBoqIds = [...new Set((refLines || []).map((l) => l.boq_item_id))]
            const { data: refItems } = refBoqIds.length
              ? await supabase.from('boq_item').select('id, description').in('id', refBoqIds)
              : { data: [] }
            const descByBoqId = Object.fromEntries((refItems || []).map((it) => [it.id, it.description]))
            refDescByLineId = Object.fromEntries((refLines || []).map((l) => [l.id, descByBoqId[l.boq_item_id]]))
            refTypeByLineId = Object.fromEntries((refLines || []).map((l) => [l.id, l.resource_library?.resource_type]))
          }

          for (const r of lineRows || []) {
            if (r.method === 'all_in_subcontract') {
              // A genuine subcontract lump-sum rate -- 'subcontract' is
              // the exact natural type, not an approximation.
              resources.push({
                row_type: 'subcontract', resource_code: null,
                resource_description: 'All-in subcontract rate — no resource breakdown',
                qty: 1, unit: null, unit_rate: r.selected_rate, total: costByLineId[r.id] ?? null,
              })
            } else if (r.method === 'factored') {
              resources.push({
                row_type: refTypeByLineId[r.factored_reference_line_id] || 'material', resource_code: null,
                resource_description: `${r.factored_pct}% of ${refDescByLineId[r.factored_reference_line_id] || 'referenced line'}`,
                qty: r.factored_pct, unit: '%', unit_rate: null, total: costByLineId[r.id] ?? null,
              })
            } else if (r.method === 'first_principles') {
              // Crew-based pricing: 'labour' is the natural
              // classification (a crew is fundamentally a labour-led
              // resource grouping, even where it also includes
              // equipment members).
              resources.push({
                row_type: 'labour', resource_code: null,
                resource_description: 'Crew-based rate',
                qty: 1, unit: null, unit_rate: fpRateByLineId[r.id] ?? null, total: costByLineId[r.id] ?? null,
              })
            } else if (r.resource_quantity_output_id && !r.resource_library) {
              // Output-Based line (Quantification module output) with a
              // manually selected rate and no resource_library record.
              // Defaults to 'material' as the most common case for
              // module-derived quantities.
              resources.push({
                row_type: 'material', resource_code: null,
                resource_description: 'Quantification module output — no resource breakdown',
                qty: 1, unit: null, unit_rate: r.selected_rate, total: costByLineId[r.id] ?? null,
              })
            }
          }
        }
        direct.push({
          boq_item_id: bi.id,
          item_number: bi.item_no,
          activity: {
            description: bi.description, unit: bi.unit, qty: bi.resolved_quantity,
            unit_rate: rateByItem[bi.id] ?? null,
            total_budget: dcByItem[bi.id] ?? 0,
            cost_code: bi.canonical_cost_code_id ? (matchedById[bi.canonical_cost_code_id] ?? null) : null,
          },
          resources,
        })
      }
      setPayload({ direct_budget: direct, indirect_budget: [], validation: { errors: [], warnings: [], info: [] } })
    } else {
      setPayload(null)
    }

    const { data: cc } = await supabase
      .from('canonical_cost_code')
      .select('id, code, description')
      .order('code')
    setCostCodes(cc || [])
  }, [revisionId])

  useEffect(() => { load() }, [load])

  // Cost Code assignment: writes only canonical_cost_code_id on the
  // matching boq_item, via the existing persistence path used
  // throughout Estimation. Never touches resolved_quantity, rate
  // fields, or any calculation table. Re-runs the read-only RPC
  // afterward purely to refresh the displayed cost_code label -- it
  // does not recompute anything.
  async function handleAssignCostCode(boqItemId, costCodeId) {
    setSavingItem(boqItemId)
    setError(null)
    setSuccessMsg(null)
    const { error } = await supabase
      .from('boq_item')
      .update({ canonical_cost_code_id: costCodeId || null })
      .eq('id', boqItemId)
    if (error) setError(error.message)
    else { setSuccessMsg('Cost Code saved.'); await load() }
    setSavingItem(null)
  }

  function toggle(itemNo) {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(itemNo) ? next.delete(itemNo) : next.add(itemNo)
      return next
    })
  }

  if (freezeSnapshotId === undefined) return <SkeletonRows rows={4} cols={3} />

  const directBudget = payload?.direct_budget || []
  const errs = payload?.validation?.errors || []
  const warns = payload?.validation?.warnings || []

  // Serializes exactly the data already rendered on this page (the
  // same `directBudget` array from the authoritative payload) into the
  // proven A:K CostSpace tender_direct_items contract. No financial
  // value is recalculated -- every cell is a direct read from
  // activity.* / resource.* fields already sourced from BOQ SSOT,
  // boq_item_direct_unit_rate, and rate_buildup_line_cost.
  function handleExportExcel() {
    const rows = []
    for (const row of directBudget) {
      rows.push([
        row.item_number, row.activity?.description, '', row.activity?.cost_code || '',
        'activity', '', '',
        row.activity?.qty, row.activity?.unit, row.activity?.unit_rate, row.activity?.total_budget,
      ])
      for (const res of row.resources || []) {
        rows.push([
          row.item_number, '', res.resource_description || '', row.activity?.cost_code || '',
          res.row_type, '', res.resource_code || '',
          res.qty, res.unit, res.unit_rate, res.total,
        ])
      }
    }
    const headers = ['Item No.', 'Description', 'Resource Description', 'Cost Code',
                      'Resource', 'code', 'Resource Code', 'Qty', 'Unit', 'Rate', 'Total']
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    ws['!cols'] = [{ wch: 8 }, { wch: 30 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 16 }, { wch: 10 }, { wch: 8 }, { wch: 10 }, { wch: 14 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Direct Budget')
    XLSX.writeFile(wb, `direct_budget_${tenderId}_${revisionId}.xlsx`)
  }
  // Display-only sums over already-computed values -- not a
  // recalculation of any cost. The authoritative per-activity total is
  // activity.total_budget from the RPC; this is purely SUM() for the
  // header summary.
  const totalDirectBudget = directBudget.reduce((s, r) => s + (r.activity?.total_budget || 0), 0)
  const totalResources = directBudget.reduce((s, r) => s + (r.resources?.length || 0), 0)

  return (
    <div>
      <EstimationBreadcrumb to={`/estimation/tender/${tenderId}/revision/${revisionId}/lifecycle`} label={'\u2190 Back to Validation Gate'} />

      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>Direct Budget Review</h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 24px' }}>
        Cost Control review and coding layer. Values shown are read-only from the Estimation export contract; only Cost Code is editable here.
      </p>

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {freezeSnapshotId === null && (
        <div style={{ padding: '10px 16px', background: '#F1F5F9', color: colors.textSecondary, borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
          📝 Draft mode — showing live Estimation data. Once this revision is frozen, this page will read the immutable snapshot instead.
        </div>
      )}

      {freezeSnapshotId !== undefined && !payload && <SkeletonRows rows={3} cols={4} />}

      {payload && (
        <>
          <div style={{ display: 'flex', gap: 32, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <SummaryFigure label="Total Direct Budget" value={formatMoney(totalDirectBudget, tenderCurrency)} />
            <SummaryFigure label="Activities" value={String(directBudget.length)} />
            <SummaryFigure label="Resources" value={String(totalResources)} />
            <SummaryFigure label="Errors" value={String(errs.length)} />
            <SummaryFigure label="Warnings" value={String(warns.length)} />
            <div style={{ marginLeft: 'auto' }}>
              <Button onClick={handleExportExcel} disabled={errs.length > 0 || directBudget.length === 0}>
                📥 Export Excel
              </Button>
              {errs.length > 0 && (
                <div style={{ fontSize: 11, color: '#991B1B', marginTop: 4 }}>Export blocked — resolve errors first.</div>
              )}
            </div>
          </div>

          {errs.length > 0 && (
            <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 20, background: '#FEE2E2', color: '#991B1B', fontSize: 13 }}>
              🚫 {errs.length} validation error{errs.length !== 1 ? 's' : ''} exist upstream — resolve on the Validation Gate before this Budget is export-ready.
            </div>
          )}

          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'auto' }}>
            {directBudget.length === 0 ? (
              <div style={{ padding: 20 }}><EmptyState title="No exportable Direct Budget rows" /></div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 960 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                    <Th></Th><Th>Item No. / Type</Th><Th>Description</Th><Th>Resource Code</Th><Th align="right">Qty</Th><Th>Unit</Th><Th align="right">Rate</Th><Th>Cost Code</Th><Th align="right">Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {directBudget.map((row, i) => {
                    const isOpen = expanded.has(row.item_number)
                    const boqItemId = row.boq_item_id // present when the RPC includes it; falls back gracefully otherwise
                    return (
                      <>
                        <tr key={`a${i}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <Td>
                            {row.resources?.length > 0 && (
                              <span style={{ cursor: 'pointer' }} onClick={() => toggle(row.item_number)}>{isOpen ? '▾' : '▸'}</span>
                            )}
                          </Td>
                          <Td>{row.item_number}</Td>
                          <Td muted>{row.activity?.description}</Td>
                          <Td></Td>
                          <Td align="right">{formatNumber(row.activity?.qty)}</Td>
                          <Td muted>{row.activity?.unit}</Td>
                          <Td align="right">{formatMoney(row.activity?.unit_rate, tenderCurrency)}</Td>
                          <Td>
                            <CostCodePicker
                              value={row.activity?.cost_code}
                              costCodes={costCodes}
                              disabled={!boqItemId || savingItem === boqItemId}
                              onChange={(ccId) => boqItemId && handleAssignCostCode(boqItemId, ccId)}
                            />
                          </Td>
                          <Td align="right">{formatMoney(row.activity?.total_budget, tenderCurrency)}</Td>
                        </tr>
                        {/* Resource rows: NEVER show Activity Cost Code
                            here -- Cost Code belongs to the Activity/
                            BOQ item level only. Resource Code and Total
                            come verbatim from the RPC/live-SSOT read
                            (resource_library.code and
                            rate_buildup_line_cost.line_cost); nothing
                            is computed here as qty*rate. */}
                        {isOpen && (row.resources || []).map((res, j) => (
                          <tr key={`a${i}r${j}`} style={{ borderBottom: `1px solid ${colors.border}`, background: '#FAFBFC' }}>
                            <Td></Td>
                            <Td style={{ paddingLeft: 24, fontWeight: 600, textTransform: 'capitalize' }}>{res.row_type || '—'}</Td>
                            <Td muted>{res.resource_description || '—'}</Td>
                            <Td muted>{res.resource_code || '—'}</Td>
                            <Td align="right">{formatNumber(res.qty)}</Td>
                            <Td muted>{res.unit || '—'}</Td>
                            <Td align="right">{formatMoney(res.unit_rate, tenderCurrency)}</Td>
                            <Td></Td>
                            <Td align="right">{formatMoney(res.total, tenderCurrency)}</Td>
                          </tr>
                        ))}
                      </>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// Reuses the existing canonical_cost_code hierarchy loaded by the
// parent. Selecting a value only calls the onChange callback, which
// writes canonical_cost_code_id -- it never touches any numeric field.
function CostCodePicker({ value, costCodes, disabled, onChange }) {
  const matched = costCodes.find((c) => c.code === value)
  return (
    <Select
      value={matched?.id || ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{ minWidth: 160, fontSize: 12 }}
    >
      <option value="">{value ? `⚠ ${value} (unmatched)` : '⚠ Not assigned'}</option>
      {costCodes.map((cc) => (
        <option key={cc.id} value={cc.id}>{cc.code} — {cc.description}</option>
      ))}
    </Select>
  )
}

function SummaryFigure({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>{value}</div>
    </div>
  )
}
function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function Td({ children, align = 'left', muted, style }) {
  return <td style={{ textAlign: align, padding: '11px 16px', fontSize: 13, color: muted ? colors.textMuted : colors.textPrimary, ...style }}>{children}</td>
}
